import { useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { useStoreMap, useUnit } from 'effector-react';
import classNames from 'classnames';
import { Button, ButtonSize, ButtonType } from '@nx-frontend/shared/ui/button';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { UiLabel } from '@nx-frontend/shared/ui/label';
import { Tooltip } from '@nx-frontend/shared/ui/tooltip';
import { UserPreview } from '@nx-frontend/shared/ui/user-preview';
import { getMemberByIdParams } from '@nx-frontend/shared/store/org-members';
import { getUserByIdParams } from '@nx-frontend/shared/store/users-store';
import {
  OrgMember,
  OrgMemberRole,
  OrgMemberRoleLabelLocKeyMap,
  PortalSettingsAccessConsole,
} from '@nx-frontend/shared/types';
import { UiTooltipPosition } from '@nx-frontend/shared/types';
import { getUserDisplayName } from '@nx-frontend/shared/utils';
import { $context, $menuItem, $portalConsoleAccess } from '../store';

export function NodeAccessItem({
  memberId,
  isLast,
  onRemove,
}: {
  memberId: string;
  isLast?: boolean;
  onRemove: (userId: number) => void;
}) {
  const t = useTranslations();
  const member = useStoreMap(getMemberByIdParams(memberId));
  const user = useStoreMap(getUserByIdParams(member.userId));
  const [access, menuItem, context] = useUnit([$portalConsoleAccess, $menuItem, $context]);
  const [actionsVisible, setActionsVisible] = useState(false);

  const userName = user ? getUserDisplayName(user, 0) : 'Anonymous User';

  const memberCanBeDeleted = canBeDeleted(member, access);

  const tooltipContent = useMemo(() => {
    if (memberCanBeDeleted) {
      return t('features.manage-members.restrict_access_to_folder');
    } else {
      return (
        <div className="w-[180px]">
          <span>
            {t.rich('features.manage-members.access_restrictions_turned_off_tooltip', {
              link: (chunk) => (
                <Link
                  href={`/portal/${context.portalId}/settings/access`}
                  className="text-teal-500 hover:text-teal-400 ml-1 border-b border-teal-400"
                >
                  {chunk}
                </Link>
              ),
            })}
          </span>
        </div>
      );
    }
  }, [context, memberCanBeDeleted, t]);
  const handleRemoveUser = () => {
    if (menuItem?.permissions) {
      onRemove(member.userId);
      setActionsVisible(false);
    }
  };
  if (!user) return;
  return (
    <div
      className={classNames('p-2 flex items-center pr-6', {
        'border-b border-neutral-200': !isLast,
      })}
    >
      <div
        className={classNames({
          'w-[80%]': !actionsVisible,
          'w-[40%]': actionsVisible,
        })}
      >
        <UserPreview name={userName} text={user.email} avatarSize="lg" avatarUrl={user.avatar}></UserPreview>
      </div>
      <div
        className={classNames('flex items-center', {
          'w-[20%]': !actionsVisible,
          'flex justify-end w-[60%]': actionsVisible,
        })}
      >
        {!actionsVisible && (
          <div className="flex w-[70%] p-2">
            <UiLabel
              title={t(OrgMemberRoleLabelLocKeyMap[member.role])}
              className={classNames('text-center', memberCanBeDeleted ? 'bg-neutral-100' : 'bg-teal-100')}
            />
          </div>
        )}
        {!actionsVisible && (
          <div className="ml-6">
            <Tooltip message={tooltipContent} position={UiTooltipPosition.LEFT}>
              <div
                className={classNames({ 'cursor-pointer': memberCanBeDeleted })}
                onClick={() => {
                  if (memberCanBeDeleted) {
                    setActionsVisible(true);
                  }
                }}
              >
                <Icon
                  icon={IconsMap.CIRCLE_CROSSED}
                  className={classNames('w-4 h-4', {
                    'text-neutral-400': !memberCanBeDeleted,
                    'text-neutral-500': memberCanBeDeleted,
                  })}
                />
              </div>
            </Tooltip>
          </div>
        )}
        {actionsVisible && (
          <div className="flex">
            <Button
              className="mr-3"
              type={ButtonType.STROKED}
              size={ButtonSize.MD}
              onClick={() => setActionsVisible(false)}
            >
              <span>{t('features.manage-members.cancel')}</span>
            </Button>
            <Button
              type={ButtonType.STROKED}
              size={ButtonSize.MD}
              border="border border-rose-500 hover:border-rose-700"
              text="text-rose-500 hover:text-rose-700"
              onClick={() => handleRemoveUser()}
            >
              <span>{t('features.manage-members.remove_from_folder')}</span>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function canBeDeleted(member: OrgMember, access?: PortalSettingsAccessConsole): boolean {
  if (!access) {
    return false;
  }
  if (member.role === OrgMemberRole.OWNER && access.owner) {
    return false;
  } else if (member.role === OrgMemberRole.MANAGER && access.managers) {
    return false;
  } else if (member.role === OrgMemberRole.MEMBER && access.members) {
    return false;
  } else if (member.role === OrgMemberRole.GUEST && access.guests) {
    return false;
  }
  return true;
}

export default NodeAccessItem;
